import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { useOrganizationStore } from '@/stores/organization-store';
import { getUserOrganizations } from '@/services/organization/api';
import type { Organization } from '@/types/multi-tenant';

/**
 * Hook to automatically sync organization context from authenticated user
 * This eliminates the need for manual organization ID setup
 *
 * It also hydrates the auth store's `currentOrganization` (the richer org
 * record) by fetching the user's organizations after login. As of Phase 1 the
 * plan itself arrives in the login response (`user.subscriptionPlan`), so
 * plan-gated features no longer depend on this fetch to know the plan — this is
 * now a background enrichment (org name, full org object, and a fresher plan
 * value), not a gating dependency. It runs for every entry point (login, OAuth,
 * and a refreshed/persisted session).
 *
 * Usage: Call this hook in App.tsx or root component
 */
export function useOrganizationSync() {
  const { user, isAuthenticated } = useAuthStore();
  const currentOrganization = useAuthStore((s) => s.currentOrganization);
  const setCurrentOrganization = useAuthStore((s) => s.setCurrentOrganization);
  const setOrganizations = useAuthStore((s) => s.setOrganizations);
  const { currentOrganizationId, setOrganizationId, clearOrganization } = useOrganizationStore();
  const hydratingRef = useRef(false);

  useEffect(() => {
    // If user is authenticated and has organizationId
    if (isAuthenticated && user?.organizationId) {
      // Only set if it's different from current (avoid unnecessary updates)
      if (currentOrganizationId !== user.organizationId) {
        setOrganizationId(user.organizationId);
      }
    } else if (!isAuthenticated) {
      // Clear organization context when user logs out
      if (currentOrganizationId) {
        clearOrganization();
      }
    }
  }, [isAuthenticated, user?.organizationId, currentOrganizationId, setOrganizationId, clearOrganization]);

  // Hydrate the auth store's currentOrganization (with subscriptionPlan) so
  // plan-gated features can read the real plan instead of always seeing null.
  useEffect(() => {
    if (!isAuthenticated || !user?.organizationId) return;
    // Already hydrated for this org — nothing to do.
    if (currentOrganization?.id === user.organizationId) return;
    if (hydratingRef.current) return;
    hydratingRef.current = true;

    getUserOrganizations()
      .then((orgs) => {
        // Be defensive: the endpoint returns a bare array, but tolerate an
        // accidental { data: [...] } envelope just in case.
        const list = (Array.isArray(orgs)
          ? orgs
          : (orgs as { data?: unknown })?.data) as Organization[] | undefined;
        if (!list || list.length === 0) return;
        setOrganizations(list);
        const mine = list.find((o) => o.id === user.organizationId) ?? list[0];
        if (mine) setCurrentOrganization(mine);
      })
      .catch(() => {
        // Non-fatal: leave the plan gate closed rather than crashing the app.
      })
      .finally(() => {
        hydratingRef.current = false;
      });
  }, [
    isAuthenticated,
    user?.organizationId,
    currentOrganization?.id,
    setCurrentOrganization,
    setOrganizations,
  ]);

  // Return current state for debugging
  return {
    hasOrganization: !!currentOrganizationId,
    organizationId: currentOrganizationId,
    userOrganizationId: user?.organizationId,
    synced: currentOrganizationId === user?.organizationId,
  };
}
